import { useMemo, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { VisionCanvas } from '@/components/common/vision-canvas.tsx';
import { CollaborationInitializer } from '@/initializers/collaboration-initializer';
import type { BaseAsset } from '@akavirtual/deai-web-core';

// The default scene template, designed to preview a single animation on a fixed model.
const DEFAULT_SCENE_TEMPLATE: BaseAsset = {
  uuid: '1f2a3b4c-5d6e-4f7a-8b9c-0d1e2f3a4b5c',
  name: 'Animation Preview Scene',
  type: 'application/scene+json',
  status: 'active',
  latestVersionId: 53,
  createdAt: '2025-10-07T07:57:43.000Z',
  updatedAt: null,
  version: 1,
  data: {
    environment: {
      uuid: '7e8a36c1-2a6c-4b6a-9383-3e0e71f76827',
    },
    entities: [
      {
        name: 'MainCamera',
        transform: {
          position: [0, 1.3, -1.5],
          rotation: [0, 180, 0],
          scale: [1, 1, 1],
        },
        components: [
          {
            type: 'Camera',
            properties: {
              fieldOfView: 60,
              nearClip: 0.1,
              farClip: 1000,
            },
          },
        ],
      },
      {
        name: 'PreviewAvatar',
        transform: {
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
        },
        components: [
           {
            type: 'Avatar',
           },
          {
            type: 'Model',
            properties: {
              asset: {
                uuid: '067c7bc2-7703-4f10-a7b8-fa7b3d2cdba6',
              },
            },
          },
          {
            type: 'Animator',
            properties: {
              controller: {
                uuid: '9b0c1d2e-3f4a-4c9d-a4e3-f2a1b0c9d8e7',
                name: 'AnimationPreviewController',
                type: 'application/animator-controller+json',
                data: {
                  initialState: 'Idle',
                  animations: {
                    // The assetId prop will be injected here
                    Idle: [{ uuid: 'd0e1f2a3-b4c5-4d8e-b9f8-a7b6c5d4e3f2' }],
                  },
                  globalTransitions: [{ trigger: 'StartIdle', toState: 'Idle' }],
                  states: {
                    Idle: {
                      animation: 'Idle',
                      loop: true,
                      loopMode: 'PingPong',
                      fade: 0.6,
                    },
                  },
                },
              },
            },
          },
        ],
      },
    ],
  },
} as BaseAsset;

type VrmaPreviewProps = {
  /** The UUID of the ANIMATION asset to be injected and previewed. */
  assetId?: string;
  /** A local file to be previewed - this would be used for previewing files before upload */
  localFile?: File;
  /** An optional, custom scene definition. If not provided, a default template is used. */
  sceneTemplate?: BaseAsset;
};

export const VrmaPreview = ({
  assetId,
  localFile,
  sceneTemplate = DEFAULT_SCENE_TEMPLATE,
}: VrmaPreviewProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Create blob URL for local file if provided
  useEffect(() => {
    if (localFile) {
      const url = URL.createObjectURL(localFile);
      setBlobUrl(url);

      // Cleanup function to revoke the blob URL
      return () => {
        URL.revokeObjectURL(url);
      };
    }

    // Clean up any existing blob URL when the component unmounts or localFile changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [localFile]);

  const definition = useMemo(() => {
    if (!sceneTemplate) return null;

    // Deep copy the template to avoid direct mutation
    const newDefinition = JSON.parse(JSON.stringify(sceneTemplate));

    // Find the entity that has the 'Animator' component
    const animatorEntity = newDefinition.data?.entities?.find((entity: any) =>
      entity.components.some((component: any) => component.type === 'Animator')
    );

    if (animatorEntity) {
      const animatorComponent = animatorEntity.components.find(
        (component: any) => component.type === 'Animator'
      );

      const controller = animatorComponent?.properties?.controller;
      
      if (controller?.data?.animations?.Idle?.[0]) {
        if (localFile && blobUrl) {
          // Generate a random UUID for the local file
          const localFileUuid = uuidv4();
          
          // Inject the local file's blob URL as URI into the animation asset
          controller.data.animations.Idle[0].uuid = localFileUuid;
          
          // Set the type to ensure the proper loader is used
          controller.data.animations.Idle[0].type = 'animation/vrma+gltf';
          
          // Add the URI to the controller's data so the system can load it
          if (!controller.data.animations.Idle[0].data) {
            controller.data.animations.Idle[0].data = {};
          }
          controller.data.animations.Idle[0].data.uri = blobUrl;
        } else if (assetId) {
          // For asset ID, just inject the UUID and type
          controller.data.animations.Idle[0].uuid = assetId;
        }
      }
    }

    return newDefinition as BaseAsset;
  }, [sceneTemplate, assetId, localFile, blobUrl]);

  if (!definition) {
    return (
      <div className="flex h-full w-full items-center justify-center text-destructive">
        Invalid Scene Template or Asset ID provided.
      </div>
    );
  }

  return (
    <VisionCanvas
      key={definition.uuid}
      instanceId={`animation-preview-${definition.uuid}`}
      className="h-full w-full"
      definition={definition}
      initializer={CollaborationInitializer}
      assetBaseUrl={window.location.origin}
    />
  );
};